import axios from "axios";
import { postRefreshToken, postLogout } from "../helpers/backend_helper";
import { toast } from "react-toastify";

// Create a dedicated axios instance for refresh token
const refreshAxios = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Function to clear auth and redirect to login
const clearAuthAndRedirect = () => {
  localStorage.removeItem("authUser");
  localStorage.removeItem("token");
  localStorage.removeItem("branchName");
  window.location.href = "/login";
};

// Response interceptor for handling token refresh
refreshAxios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If error is not 401 or request was retry, reject immediately
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // If this is the refresh token request itself failing, logout
    // Check both full URL and relative path
    const isRefreshRequest =
      originalRequest.url?.includes("/refresh-token") ||
      originalRequest.url?.includes("Auth%2Frefresh-token") ||
      originalRequest.url?.includes("Auth/refresh-token");

    if (isRefreshRequest) {
      clearAuthAndRedirect();
      return Promise.reject(error);
    }

    // If already refreshing, add to queue
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          originalRequest.headers["Authorization"] = `Bearer ${token}`;
          return refreshAxios(originalRequest);
        })
        .catch((err) => {
          return Promise.reject(err);
        });
    }

    // Mark as refreshing
    originalRequest._retry = true;
    isRefreshing = true;

    // Get current tokens
    const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
    const currentAccessToken = localStorage.getItem("token");
    const currentRefreshToken = authUser.refreshToken;

    if (!currentRefreshToken) {
      clearAuthAndRedirect();
      return Promise.reject(error);
    }

    try {
      console.log("[Token Refresh] Attempting to refresh token...");

      // Call refresh token endpoint
      const response = await postRefreshToken({
        accessToken: currentAccessToken,
        refreshToken: currentRefreshToken,
      });

      console.log("[Token Refresh] Success! Got new tokens.");

      // Extract new tokens from response
      const newAccessToken = response.accessToken;
      const newRefreshToken = response.refreshToken;
      const updatedAuthUser = {
        ...authUser,
        ...response,
      };

      // Update localStorage
      localStorage.setItem("authUser", JSON.stringify(updatedAuthUser));
      localStorage.setItem("token", newAccessToken);

      // Update authorization header for original request
      originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;

      // Process queued requests
      processQueue(null, newAccessToken);

      console.log("[Token Refresh] Retrying original request.");

      // Retry original request
      return refreshAxios(originalRequest);
    } catch (refreshError) {
      console.error("[Token Refresh] Failed:", refreshError);

      // Refresh failed - logout user
      processQueue(refreshError, null);

      // Try to call logout endpoint
      try {
        await postLogout();
      } catch (logoutError) {
        console.error("Logout failed:", logoutError);
      }

      // Clear auth and redirect
      clearAuthAndRedirect();

      toast.error("Session expired. Please login again.");
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

// Request interceptor to add authorization header
refreshAxios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export default refreshAxios;
